/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { buildConversationEndpointUrl } from 'src/core/utils/endpointBuilder/buildConversationEndpointUrl';
import { EndpointUrlParams } from 'src/core/utils/endpointBuilder/types';
import { createTestPanePredictionStore } from 'src/luis/core/stores/testPanePredictionStore';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { endpointSwaggerToUtteranceStore } from 'src/luis/projects/testPane/services/endpointPredictionMapper/endpointPredictionMappers';
import { getUtteranceTrainedPredictions } from 'src/luis/projects/testPane/services/endpointPredictionMapper/utterancePredictionMapper';
import { trainedPredictionSwaggerToPredictionStore } from 'src/luis/projects/testPane/services/mappers';
import { EndpointPredictionSwagger } from 'src/luis/projects/testPane/types/swagger/EndpointPredictionSwagger';
import { UtteranceResultStore } from 'src/luis/projects/testPane/types/types';
import { PredictionSwagger } from 'src/luis/projects/utterances/services/mappers';
import { TestPanePredictionStore } from 'src/luis/types/stores/testPane/TestPanePredictionStore';

/**
 * Represents a service to perform CRUD operations for single test.
 *
 * @param appId The app id to get the batchTest for.
 */
export const getSingleTestService = createService(({ context, getEnvironment }) => {
    const { webApiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const basicUrl = (text: string) =>
        urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('predict')
            .queryParam('example', text)
            .queryParam('patternDetails', 'true')
            .queryParam('multiple-intents', 'true');

    /**
     * Predicts an utterance.
     */
    const predict = async ({ text, entities }: { text: string; entities: Entity[] }): Promise<TestPanePredictionStore> => {
        const enabledConstraintsUrl = basicUrl(text).build();
        const disabledConstraintsUrl = basicUrl(text)
            .queryParam('disableConstraints', 'true')
            .build();

        const [{ data: disabledConstraintsPrediction }, { data: enabledConstraintsPrediction }] = await Promise.all([
            httpService.get<PredictionSwagger>(disabledConstraintsUrl, apiOptions),
            httpService.get<PredictionSwagger>(enabledConstraintsUrl, apiOptions)
        ]);

        const utteranceWithConstraintsResult = trainedPredictionSwaggerToPredictionStore(enabledConstraintsPrediction);
        const utteranceWithoutConstraintsResult = trainedPredictionSwaggerToPredictionStore(disabledConstraintsPrediction);

        const utteranceResultDto = getUtteranceTrainedPredictions(
            utteranceWithConstraintsResult,
            entities,
            utteranceWithoutConstraintsResult
        );

        return createTestPanePredictionStore(utteranceResultDto);
    };

    /**
     * Gets the predictions of the published application.
     */
    const predictEndpoint = async ({
        params
    }: {
        params: EndpointUrlParams;
    }): Promise<[UtteranceResultStore, EndpointPredictionSwagger]> => {
        const url = buildConversationEndpointUrl(params, true);

        const predictApiOptions = luisRequestOptionsBuilder()
            .useContentType('json')
            .useDefaultOptions(context.serviceKey, isProduction)
            .build();

        const { data: prediction } = await httpService.get<EndpointPredictionSwagger>(url, predictApiOptions);

        return [endpointSwaggerToUtteranceStore(prediction), prediction];
    };

    return { predict, predictEndpoint };
});
